/** @jsx jsx */
import { ReactFC } from '@frontend/utils/react-compat'
import { css, jsx } from '@emotion/react'
import styled from '@emotion/styled'
import { format, parseISO } from 'date-fns'
import ja from 'date-fns/locale/ja'
import React, { CSSProperties, type JSX } from 'react'
import { FacilityAvailableStatusCode } from '../../entites/FacilityInfo'
import { Icon } from '../../utils/Icon'
import { color, fontSize } from '../../utils/Style'
import { CircleStatusIcon } from '../CircleStatusIcon'

export type ReservationDateCardProps = {
  dateString: string
  holidayClass: string
  statusCode: FacilityAvailableStatusCode
  statusLabel: string
  statusSupplement: string
  reservationPageUrl: string
  acceptanceTime?: string
  applicationStartTimeText: string | null
  applicationEndTimeText: string | null
  note?: string
  averageCanceledCount: number | null
  reservationsCountOverCapacityWhenCongestionOrFull: number | null
  style?: CSSProperties
}

export const ReservationDateCard: ReactFC<ReservationDateCardProps> = (
  props,
) => {
  const {
    dateString,
    holidayClass,
    statusCode,
    statusLabel,
    note,
    reservationPageUrl,
    style,
  } = props

  const date = parseISO(dateString)
  const reservationDisable =
    statusCode === 'before_acceptance' ||
    statusCode === 'end_acceptance' ||
    statusCode === 'holiday' ||
    statusCode === 'not_contracted'

  return (
    <Card style={style}>
      <DateWrapper dateTime={dateString} className={holidayClass}>
        <Year>
          {format(date, 'yyyy')}
          <span className="sr-only">/</span>
        </Year>
        <Date>
          {format(date, 'MM')}/{format(date, 'dd')}
        </Date>
        <WeekdayWrapper className={holidayClass}>
          <span>
            {format(date, 'eee', {
              locale: ja,
            })}
          </span>
        </WeekdayWrapper>
      </DateWrapper>
      <AvailabilityStatus
        statusCode={statusCode}
        statusLabel={statusLabel}
        style={{ marginTop: '12px', marginBottom: '20px' }}
      />
      <ApplicationStartTime {...props} />
      <Warning {...props} />
      <StatusSupplement {...props} />
      <ReservationLink
        href={reservationPageUrl}
        disable={reservationDisable}
        css={css`
          margin-top: 25px;
        `}
      >
        <span>予約を申し込む</span>
      </ReservationLink>
      <ApplicationEndTime {...props} />
      {note && note.trim() !== '<p></p>' && (
        <React.Fragment>
          <Hr
            css={css`
              margin-top: 20px;
              margin-left: -20px;
              margin-right: -20px;
            `}
          />
          <Note
            css={css`
              margin-top: 20px;
            `}
            dangerouslySetInnerHTML={{ __html: note }}
          ></Note>
        </React.Fragment>
      )}
    </Card>
  )
}

const ApplicationStartTime: ReactFC<ReservationDateCardProps> = ({
  statusCode,
  applicationStartTimeText,
}) => {
  if (statusCode !== 'before_acceptance') return null

  return (
    <ApplicationTimeRoot css={{ marginTop: '20px' }}>
      <Icon iconName="time" style={{ width: '14px', height: '14px' }} />
      <ApplicationTimeText>
        受付開始
        <span css={{ color: '#000' }}>　{applicationStartTimeText}</span>
      </ApplicationTimeText>
    </ApplicationTimeRoot>
  )
}
const ApplicationEndTime: ReactFC<ReservationDateCardProps> = ({
  statusCode,
  applicationEndTimeText,
}) => {
  const showApplicationEndTime =
    statusCode !== 'before_acceptance' &&
    statusCode !== 'end_acceptance' &&
    statusCode !== 'holiday' &&
    statusCode !== 'not_contracted' &&
    applicationEndTimeText

  if (!showApplicationEndTime) return null
  return (
    <ApplicationTimeRoot
      css={{
        marginTop: '20px',
      }}
    >
      <Icon iconName="time" style={{ width: '14px', height: '14px' }} />
      <ApplicationTimeText>
        受付終了　{applicationEndTimeText}
      </ApplicationTimeText>
    </ApplicationTimeRoot>
  )
}

const ApplicationTimeRoot = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 3px;
  align-self: stretch;
`

const ApplicationTimeText = styled.div`
  color: ${color.colorGray};
  text-align: center;

  /* Typo-size-3 */
  font-size: ${fontSize._12};
  font-style: normal;
  font-weight: 400;
  line-height: 160%; /* 19.2px */
`

const Card = styled.div(css`
  padding: 25px 20px 20px;
  background: #ffffff;
  box-shadow: 0px 2px 6px rgba(0, 0, 0, 0.08);
  border-radius: 10px;
`)

const DateWrapper = styled.time(css`
  display: grid;
  grid-auto-flow: column;
  column-gap: 7px;
  align-items: center;
  justify-content: center;
  color: black;
  margin-left: -8px;
  margin-right: -8px;

  &.saturday {
    color: #23a7c3;
  }
  &.sunday,
  &.holiday {
    color: #de8900;
  }
`)

const WeekdayWrapper = styled.span(css`
  display: block;
  width: 18px;
  height: 18px;
  border: 1px solid #000000;
  border-radius: 50%;
  position: relative;

  & > * {
    position: absolute;
    left: 0;
    right: 0;
    top: 0;
    bottom: 0;
    margin: auto;
    font-weight: bold;
    font-size: 10px;
    line-height: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  &.saturday {
    color: #23a7c3;
    border-color: #23a7c3;
  }
  &.sunday,
  &.holiday {
    color: white;
    border-color: #de8900;
    background: #de8900;
  }
`)

const Year = styled.span(css`
  display: block;
  font-weight: bold;
  font-size: 14px;
  line-height: 150%;
  white-space: nowrap;
`)

const Date = styled.span(css`
  display: block;
  font-weight: bold;
  font-size: 24px;
  line-height: 100%;
  white-space: nowrap;
`)

const AvailabilityStatus: ReactFC<{
  statusCode: ReservationDateCardProps['statusCode']
  statusLabel: ReservationDateCardProps['statusLabel']
  style?: CSSProperties
}> = ({ statusCode, statusLabel, style }) => {
  let color = '#3f3d35'
  switch (statusCode) {
    case 'vacancies':
      color = '#0cb200'
      break
    case 'vacancies_little':
      color = '#de8900'
      break
    case 'congestion':
    case 'full':
      color = '#a80046'
      break
  }

  const rootCss = css`
    display: flex;
    align-items: center;
    justify-content: center;
    column-gap: 8px;
    font-weight: bold;
    font-size: ${fontSize._16};
    line-height: 125%;
    white-space: nowrap;
    margin-left: -8px;
    margin-right: -8px;
  `

  if (statusCode === 'end_acceptance')
    return (
      <div css={rootCss} style={style}>
        <EndAcceptanceLabel />
      </div>
    )
  if (statusCode === 'before_acceptance')
    return (
      <div css={rootCss} style={style}>
        <BeforeAcceptanceLabel />
      </div>
    )
  if (statusCode === 'holiday')
    return (
      <div css={rootCss} style={style}>
        <HolidayLabel />
      </div>
    )

  return (
    <div css={rootCss} style={style}>
      <CircleStatusIcon statusCode={statusCode} size="semismall" />
      <span css={{ color }}>{statusLabel}</span>
    </div>
  )
}

const EndAcceptanceLabel: ReactFC = () => {
  return (
    <div
      css={css`
        display: flex;
        padding: 1px 4px;
        justify-content: center;
        align-items: center;
        gap: 8px;

        border-radius: 4px;
        border: 1px solid #6b383c;
        background: #f6eeef;
      `}
    >
      <span
        css={css`
          color: #6b383c;
          font-feature-settings:
            'clig' off,
            'liga' off;

          /* 基本/Body Bold */
          font-size: ${fontSize._16};
          font-style: normal;
          font-weight: 700;
          line-height: 160%; /* 25.6px */
        `}
      >
        受付終了
      </span>
    </div>
  )
}
const BeforeAcceptanceLabel: ReactFC = () => {
  return (
    <div
      css={css`
        display: flex;
        padding: 1px 4px;
        align-items: flex-start;
        gap: 10px;

        border-radius: 4px;
        border: 1px solid ${color.colorAlertSuccess};
        background: #f1fff0;
      `}
    >
      <span
        css={css`
          color: ${color.colorAlertSuccess};
          font-feature-settings:
            'clig' off,
            'liga' off;

          /* 基本/Body Bold */
          font-size: ${fontSize._16};
          font-style: normal;
          font-weight: 700;
          line-height: 160%; /* 25.6px */
        `}
      >
        受付開始前
      </span>
    </div>
  )
}
const HolidayLabel: ReactFC = () => {
  return (
    <div
      css={css`
        display: flex;
        padding: 1px 4px;
        justify-content: center;
        align-items: center;
        gap: 8px;

        border-radius: 4px;
        border: 1px solid ${color.colorHolidayLabel};
        background: #fff6f5;
      `}
    >
      <span
        css={css`
          color: ${color.colorHolidayLabel};
          font-feature-settings:
            'clig' off,
            'liga' off;

          /* 基本/Body Bold */
          font-size: ${fontSize._16};
          font-style: normal;
          font-weight: 700;
          line-height: 160%; /* 25.6px */
        `}
      >
        休室日
      </span>
    </div>
  )
}

const Warning: ReactFC<ReservationDateCardProps> = ({
  statusCode,
  reservationsCountOverCapacityWhenCongestionOrFull,
  averageCanceledCount,
}) => {
  if (
    statusCode === 'before_acceptance' ||
    statusCode === 'end_acceptance' ||
    statusCode === 'holiday' ||
    statusCode === 'not_contracted'
  )
    return null

  const showAverageCanceledCount =
    averageCanceledCount !== undefined &&
    averageCanceledCount !== null &&
    (statusCode === 'full' || statusCode === 'congestion')

  const showReservationsCountOverCapacityWhenCongestionOrFull =
    reservationsCountOverCapacityWhenCongestionOrFull !== undefined &&
    reservationsCountOverCapacityWhenCongestionOrFull !== null

  if (statusCode == 'full' || statusCode == 'congestion')
    return (
      <WarningRoot>
        <p css={{ padding: '10px' }}>定員以上の予約申し込みが入っています。</p>
        {showReservationsCountOverCapacityWhenCongestionOrFull && (
          <p
            css={css`
              color: ${color.colorBlack};
              text-align: center;
              font-size: ${fontSize._12};
              font-style: normal;
              font-weight: 400;
              line-height: 160%; /* 19.2px */
            `}
          >
            現在の申込人数：
            <span
              css={css`
                color: ${color.colorBlack};
                font-size: ${fontSize._12};
                font-style: normal;
                font-weight: 700;
                line-height: 160%;
              `}
            >
              {reservationsCountOverCapacityWhenCongestionOrFull !== 0
                ? `定員＋${reservationsCountOverCapacityWhenCongestionOrFull}人`
                : '定員ちょうど'}
            </span>
          </p>
        )}
        {showAverageCanceledCount && (
          <p
            css={css`
              color: var(--monochrome-ultra-dark-gray, #3f3d35);
              text-align: center;
              font-size: ${fontSize._12};
              font-style: normal;
              font-weight: 400;
              line-height: 160%; /* 19.2px */
            `}
          >
            この施設の普段のキャンセル数は{averageCanceledCount}人ほどです。
          </p>
        )}
      </WarningRoot>
    )

  return (
    <WarningRoot>
      利用できない場合もあります。
      <br />
      <a
        href="https://goodbaton.notion.site/76772c6d2860430a824a5aeb670e593f"
        target="_blank"
        rel="noopener noreferrer"
        css={css`
          color: ${color.colorBlack};
          font-size: ${fontSize._12};
          font-style: normal;
          font-weight: 400;
          line-height: 160%;
          text-decoration-line: underline;
        `}
      >
        なぜ利用できないことがあるの？
      </a>
    </WarningRoot>
  )
}

const WarningRoot = styled.div(css`
  display: flex;
  padding: 10px;
  flex-direction: column;
  align-items: center;
  gap: 5px;
  align-self: stretch;

  border-radius: 10px;
  background: #fffcee;

  color: ${color.colorBlack};
  text-align: center;
  font-size: ${fontSize._12};
  font-style: normal;
  font-weight: 700;
  line-height: 160%; /* 19.2px */
`)

type ReservationLinkProps = JSX.IntrinsicElements['a'] & {
  disable?: boolean
}

const ReservationLink = styled.a(
  (props: ReservationLinkProps) => css`
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0px 3px 8px rgba(0, 0, 0, 0.08);
    border-radius: 30px;
    padding: 15px 0px 14px;
    color: white;
    font-weight: bold;
    font-size: ${fontSize._16};
    line-height: 130%;
    text-decoration: none;
    background: ${props.disable ? '#9f9e98' : '#eb2d23'};
    cursor: ${props.disable ? 'unset' : 'pointer'};
    pointer-events: ${props.disable ? 'none' : 'inherit'};
  `,
)

const StatusSupplement: ReactFC<ReservationDateCardProps> = ({
  statusCode,
}) => {
  if (statusCode === 'end_acceptance')
    return (
      <StatusSupplementRoot>本日の受付は終了しました。</StatusSupplementRoot>
    )
  if (statusCode === 'holiday')
    return <StatusSupplementRoot>施設がお休みの日です。</StatusSupplementRoot>

  return null
}

const StatusSupplementRoot = styled.p(css`
  color: ${color.colorBlack};
  text-align: center;
  font-size: ${fontSize._12};
  font-style: normal;
  font-weight: 700;
  line-height: 160%; /* 19.2px */
`)

const Hr = styled.hr(css`
  border-top: 1px solid #f6f5f4;
`)

const Note = styled.div(css`
  font-size: 14px;
  line-height: 160%;
  color: black;
`)
